import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface PortalUIProps {
  /** True if portal should request close when pressing esc */
  uiCloseOnEsc?: boolean;
  /** True if portal should request close when outside clicking */
  uiCloseOnOutsideClick?: boolean;
  /** Class name to assign to the portal root node */
  className?: string;

  /** Before opening callback */
  uiOnOpening?(): void;
  /** After opening callback */
  uiOnOpened?(node: HTMLElement): void;
  /** Before closing callback */
  uiOnClosing?(node: HTMLElement): void;
  /** Closed callback */
  uiOnClosed?(): void;

  /**
   * Portal request close callback
   * (esc pressed or outside click/touch)
   */
  uiOnRequestClose(event: Event): void;

  /** Portal content */
  children?: ReactNode;
}

export type PortalProps = PortalUIProps;

export default function Portal({
  uiCloseOnEsc = true,
  uiCloseOnOutsideClick = true,
  className,
  uiOnOpening,
  uiOnOpened,
  uiOnClosing,
  uiOnClosed,
  uiOnRequestClose,
  children,
}: PortalProps) {
  // Create the host element once.
  const host = useMemo(() => document.createElement('div'), []);
  const hostRef = useRef(host);

  // Opening lifecycle
  useEffect(() => {
    if (uiOnOpening) uiOnOpening();

    if (className) hostRef.current.className = className;
    document.body.appendChild(hostRef.current);
    if (uiOnOpened) uiOnOpened(hostRef.current);

    return () => {
      if (uiOnClosing) uiOnClosing(hostRef.current);
      document.body.removeChild(hostRef.current);
      if (uiOnClosed) uiOnClosed();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once

  // Reflect className updates
  useEffect(() => {
    hostRef.current.className = className || '';
  }, [className]);

  // ESC handler
  useEffect(() => {
    if (!uiCloseOnEsc) return;

    const onKeyDown = (e: KeyboardEvent) => {
      // ESC
      if (e.key === 'Escape' || e.keyCode === 27) {
        uiOnRequestClose(e);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [uiCloseOnEsc, uiOnRequestClose]);

  // Outside click/touch handler
  useEffect(() => {
    if (!uiCloseOnOutsideClick) return;

    const isPrimaryButton = (e: MouseEvent) => typeof e.button === 'undefined' || e.button === 0;

    const onPointerUp = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      const root = hostRef.current;

      // If event target is inside the portal subtree → ignore
      if (target && root.contains(target)) return;

      // If mouse event and not primary button → ignore
      if (e instanceof MouseEvent && !isPrimaryButton(e)) return;

      uiOnRequestClose(e as Event);
    };

    document.addEventListener('mouseup', onPointerUp);
    document.addEventListener('touchend', onPointerUp, { passive: true });

    return () => {
      document.removeEventListener('mouseup', onPointerUp);
      document.removeEventListener('touchend', onPointerUp);
    };
  }, [uiCloseOnOutsideClick, uiOnRequestClose]);

  return createPortal(children, hostRef.current);
}
